import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import language from '../../variables/language';
import usePersistedState from '../../hooks/usePersistedState';
import ConversationTemplatePicker from './ConversationTemplatePicker';
import QuizGenerationModal from './QuizGenerationModal';
import IconAdd from '../../components/icons/IconAdd';
import IconDelete from '../../components/icons/IconDelete';
import IconMessages from '../../components/icons/IconMessages';
import IconCopyUrl from '../../components/icons/IconCopyUrl';

const TABS = [
   {key: 0, label: 'Public'},
   {key: 1, label: 'Personal'},
];

const GAME_BASE_URL = 'https://games.english-from-zero.ovh/start';

// some legacy dAdd values carry a trailing IANA zone in brackets luxon
// can't parse directly (e.g. "...+02:00[Europe/Warsaw]")
const formatDate = (val) => {
   if(!val) {
      return '';
   }
   const dt = DateTime.fromISO(val.replace(/\[.*\]/, ''), { setZone: true });
   return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm') : '';
}

export default function StudentsConversation(props) {
   const navigate = useNavigate();
   const studentId = props.id;
   const lang = language[language.lang];

   const [activeTab, setActiveTab] = usePersistedState('student-conv-panel-tab', 0);
   const [progress, setProgress] = useState([]);
   const [status, setStatus] = useState('loading');
   const [refresh, setRefresh] = useState(0);
   // NOT persisted on purpose: 'algorithm' is a momentary trigger that opens
   // QuizGenerationModal (see handleModeChange below), which itself resets
   // this back to 'conversation' the instant it closes (handleQuizModalClose)
   // - by the time any reload could happen, it's already back to the
   // default, so persisting it would be dead weight, not a real preference.
   const [addMode, setAddMode] = useState('conversation');
   const [pickedId, setPickedId] = useState('');
   const [adding, setAdding] = useState(false);
   const [copiedId, setCopiedId] = useState(null);
   const [quizModalOpen, setQuizModalOpen] = useState(false);

   useEffect(() => {
      if(!studentId) {
         return;
      }
      setStatus('loading');
      axios.get('/api/conversationprogress?student=' + studentId + '&isForMe=' + activeTab, { withCredentials: true })
         .then(response => {
            setProgress(Array.isArray(response.data) ? response.data : []);
            setStatus('done');
         })
         .catch(err => {
            if(err.response && err.response.status === 401) navigate('/login');
            setStatus('error');
         });
   }, [studentId, activeTab, refresh, navigate]);

   const handleTabChange = (key) => {
      setActiveTab(key);
   }
   const handleModeChange = (mode) => {
      setAddMode(mode);
      setPickedId('');
      if(mode === 'algorithm') {
         setQuizModalOpen(true);
      }
   }

   const handleAdd = () => {
      if(!pickedId || adding) {
         return;
      }
      setAdding(true);
      axios.post('/api/conversationprogress', JSON.stringify({ student: studentId, conversationId: pickedId }),
         { withCredentials: true, headers: {'Content-Type': 'application/json'} })
      .then(() => {
         setPickedId('');
         setRefresh(prev => prev + 1);
      }).catch(err => {
         if(err.response && err.response.status === 401) navigate('/login');
         console.log(err);
      }).finally(() => setAdding(false));
   }

   const handleQuizModalClose = () => {
      setQuizModalOpen(false);
      setAddMode('conversation');
   }

   const handleDelete = (id) => {
      if(!window.confirm('Czy na pewno chcesz usunąć ten element?')) {
         return;
      }
      axios.delete('/api/conversationprogress/' + id, { withCredentials: true })
         .then(() => setRefresh(prev => prev + 1))
         .catch(err => {
            window.alert("Елемент не видалено скоріш за все він пов'язаний з іншими елементами, тож спочатку переконайтеся чи жоден елемент більше не повязаний з цим !");
            console.log(err);
         });
   }

   const handleViewMessages = (id) => {
      navigate('/messages/byconvprogress/' + id);
   }

   const handleCopyLink = (item) => {
      const hash = item.conversationId && item.conversationId.hashCode;
      if(!hash) {
         return;
      }
      const link = GAME_BASE_URL + '?student=' + studentId + '&type=1&id=' + hash;
      navigator.clipboard.writeText(link).then(() => {
         setCopiedId(item.id);
         setTimeout(() => setCopiedId(prev => prev === item.id ? null : prev), 1500);
      }).catch(() => {});
   }

   return (
      <div className='conv-panel'>
         <div className='conv-panel-tabs'>
            {TABS.map(tab => (
               <div key={tab.key} className={'conv-panel-tab' + (activeTab === tab.key ? ' active' : '')}
                  onClick={() => handleTabChange(tab.key)}>
                  {lang[tab.label]} {lang.Tests}
               </div>
            ))}
         </div>

         <div className='conv-panel-add-row'>
            <div className='conv-panel-mode-toggle'>
               <span className={'conv-panel-mode-btn' + (addMode === 'conversation' ? ' active' : '')}
                  onClick={() => handleModeChange('conversation')}>{lang.conversationId}</span>
               <span className={'conv-panel-mode-btn' + (addMode === 'algorithm' ? ' active' : '')}
                  onClick={() => handleModeChange('algorithm')}>{lang.modals.quizGeneration.title}</span>
            </div>
            {addMode === 'conversation' ? (
               <>
                  <ConversationTemplatePicker value={pickedId} onChange={setPickedId} placeholder={lang.conversationId} />
                  <div className={'student-lessons-add-btn' + (!pickedId || adding ? ' conv-panel-add-btn-disabled' : '')}
                     onClick={handleAdd} title={lang.modals.conversationprogress.createTitle}>
                     <IconAdd />
                  </div>
               </>
            ) : ''}
         </div>

         <div className='conv-panel-list'>
            {status === 'loading' ? <p className='conv-panel-empty'>{lang['Loading ...']}</p> : ''}
            {status === 'done' && progress.length === 0 ? <p className='conv-panel-empty'>{lang.noTests}</p> : ''}
            {progress.map(item => (
               <div className='conv-panel-row' key={item.id}>
                  <div className='conv-panel-row-top'>
                     <span className='conv-panel-row-name'>{item.conversationId ? item.conversationId.name : ''}</span>
                     <span className={'student-lessons-badge' + (item.isFinished === 1 ? ' on' : ' pending')}>
                        <i className='student-lessons-badge-dot'></i>{item.isFinished === 1 ? lang.isFinished : lang.inProgress}
                     </span>
                  </div>
                  <div className='conv-panel-row-bottom'>
                     <span className='conv-panel-row-date'>{formatDate(item.dAdd)}</span>
                     <div className='conv-panel-row-actions'>
                        {copiedId === item.id ? <span className='conv-panel-copied-hint'>{lang.studentsPage.copied}</span> : ''}
                        <div className='student-lessons-icon-btn' onClick={() => handleViewMessages(item.id)} title={lang.messages}>
                           <IconMessages style={{fill:'#7ec2e8', width:13, height:13, margin:0}} />
                        </div>
                        <div className='student-lessons-icon-btn' onClick={() => handleCopyLink(item)} title={lang.url}>
                           <IconCopyUrl style={{fill:'#8fc9c3', width:13, height:13, margin:0}} />
                        </div>
                        <div className='student-lessons-icon-btn' onClick={() => handleDelete(item.id)}>
                           <IconDelete style={{fill:'#e0876c', width:13, height:13, margin:0}} />
                        </div>
                     </div>
                  </div>
               </div>
            ))}
         </div>

         <QuizGenerationModal isOpen={quizModalOpen} studentId={studentId}
            onClose={handleQuizModalClose}
            onGenerated={() => setRefresh(prev => prev + 1)} />
      </div>
   );
}
